import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface UserPlanData {
  plan: string;
  subscription_status?: string;
  videos_count: number;
  videos_limit: number;
  storage_used: number;
  storage_limit: number;
  has_active_subscription: boolean;
  current_period_end?: string;
  cancel_at_period_end?: boolean;
}

export const useUserPlan = () => {
  const { userProfile, subscriptionLoading, refreshSubscriptionStatus } = useAuth();

  // Convert userProfile data to the expected format
  const planData: UserPlanData | null = useMemo(() => {
    if (!userProfile) return null;
    
    return {
      plan: userProfile.plan,
      subscription_status: userProfile.subscriptionStatus,
      videos_count: userProfile.videosProcessed,
      videos_limit: userProfile.monthlyLimit,
      storage_used: userProfile.storageUsed || 0,
      storage_limit: userProfile.storageLimit || (1024 * 1024 * 1024), // 1GB default
      has_active_subscription: userProfile.hasActiveSubscription || false,
      current_period_end: userProfile.currentPeriodEnd?.toISOString(),
      cancel_at_period_end: userProfile.cancelAtPeriodEnd || false
    };
  }, [userProfile]);

  const isPro = useMemo(() => {
    if (!planData) return false;
    // Pro includes all paid plans with active subscriptions
    return (planData.plan === 'starter_plus' || planData.plan === 'creator' || planData.plan === 'pro' || planData.plan === 'lifetime') && planData.has_active_subscription;
  }, [planData]);

  const isFree = useMemo(() => {
    // If we have user profile but no plan data yet, use the profile directly
    if (!planData && userProfile) {
      return userProfile.plan === 'free' || !userProfile.hasActiveSubscription;
    }
    if (!planData) return true; // Default to free while loading
    return planData.plan === 'free' || !planData.has_active_subscription;
  }, [planData, userProfile]);

  return {
    planData,
    loading: subscriptionLoading,
    error: null, // No error state since we're using cached data
    isPro,
    isFree,
    refetch: refreshSubscriptionStatus // Use the AuthContext refresh function
  };
}; 